/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, June 2023
 * @flow
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Box } from '@material-ui/core';
import useStyles from './useStyles';
import WaitlistCard from './WaitlistCard';
import WaitlistHeader from './WaitlistHeader';
import cn from 'classnames';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  let referenceBookingId = null;
  if (list.length > 1) {
    referenceBookingId = endIndex === 0 ? result[1]?.id : result[endIndex - 1]?.id;
  }
  const after = endIndex === 0 ? false : true;

  return {
    list: result,
    isDragged: startIndex !== endIndex,
    after,
    bookingId: list[startIndex]?.id,
    referenceBookingId,
  };
};

const getItemStyle = (isDragging, draggableStyle) => ({
  useSelect: 'none',
  ...draggableStyle,
});

const getListStyle = isDraggingOver => ({
  width: '100%',
});

const Waitlist = ({
  bookingList,
  onMoveBooking,
  stationMap,
  onBookingActionClick,
  bookingStatus,
  onChangeBookingStatus,
  bookingCount,
  onAddGuest,
  onOpenMenu,
}) => {
  const classes = useStyles();
  const [enableDrag, setEnableDrag] = React.useState(false);
  const [bookings, setBookings] = React.useState([]);
  const { storeInfo } = useSelector(state => state);
  const { id: storeId } = storeInfo?.data || {};

  React.useEffect(() => {
    setBookings(bookingList);
  }, [bookingList]);

  const onToggleEnableDrag = () => {
    setEnableDrag(enableDrag => !enableDrag);
  };

  const onDragEnd = result => {
    //dropped outside the list
    if (!result.destination) {
      return;
    }

    const { list, isDragged, after, bookingId, referenceBookingId } = reorder(
      bookings,
      result.source.index,
      result.destination.index
    );

    setBookings(list);

    if (isDragged && referenceBookingId) {
      const payload = {
        storeId,
        bookingId,
        referenceBookingId,
        after,
      };
      onMoveBooking && onMoveBooking(payload);
    }
  };

  return (
    <Box className={cn(classes.root)}>
      <WaitlistHeader
        enableDrag={enableDrag}
        onToggleEnableDrag={onToggleEnableDrag}
        bookingStatus={bookingStatus}
        onChangeBookingStatus={onChangeBookingStatus}
        onAddGuest={onAddGuest}
        bookingCount={bookingCount}
        onOpenMenu={onOpenMenu}
      />
      <Box className={classes.listContainer}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                <Bookings
                  bookings={bookings}
                  onBookingActionClick={onBookingActionClick}
                  stationMap={stationMap}
                  isEnabledDragDrop={enableDrag}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Box>
    </Box>
  );
};

export default Waitlist;

const Bookings = ({
  bookings,
  onBookingActionClick,
  stationMap,
  isEnabledDragDrop,
}) => {

  return (
    bookings.map((booking, index) => (
      <Draggable key={`${booking.id}`} draggableId={`${booking.id}`} index={index} isDragDisabled={!isEnabledDragDrop || booking.status !== 0}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={getItemStyle(
              snapshot.isDragging,
              provided.draggableProps.style
            )}
          >
            <WaitlistCard
              index={index}
              booking={booking}
              onBookingActionClick={onBookingActionClick}
              stationMap={stationMap}
            />
          </div>
        )}
      </Draggable>
    ))
  );
};
